import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface ContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const Context = createContext<ContextValue | null>(null);

export function useConfirm() {
  const ctx = useContext(Context);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx.confirm;
}

interface Pending extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => setPending({ ...opts, resolve })),
    [],
  );

  const close = (result: boolean) => {
    if (!pending) return;
    pending.resolve(result);
    setPending(null);
  };

  return (
    <Context.Provider value={{ confirm }}>
      {children}
      {pending && (
        <div
          className="dialog__backdrop"
          onClick={() => close(false)}
          onKeyDown={(e) => e.key === "Escape" && close(false)}
          role="presentation"
        >
          <div
            className="dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dialog-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="dialog-title" className="dialog__title">{pending.title}</h2>
            <p className="dialog__msg">{pending.message}</p>
            <div className="dialog__actions">
              <button className="btn btn--text" onClick={() => close(false)}>
                {pending.cancelLabel ?? "Cancel"}
              </button>
              <button
                className={`btn ${pending.danger ? "btn--danger" : "btn--primary"}`}
                onClick={() => close(true)}
                autoFocus
              >
                {pending.confirmLabel ?? "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Context.Provider>
  );
}
